import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
} from "@mui/material";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";
import { useStates } from "../../../hooks/useStates";
import useHubManagers from "../../../hooks/useHubManagers";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import CustomLoading from "../../../components/common/CustomLoading";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

const HubManagementDetails: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [hubData, setHubData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cities, setCities] = useState<Array<{city_id: number, city_name: string}>>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  
  const { states, loading: statesLoading, error: statesError } = useStates();
  const { hubManagers, loading: hubManagersLoading, error: hubManagersError } = useHubManagers();
  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  useEffect(() => {
    if (id) {
      fetchHubDetails(id);
    }
  }, [id]);

  const fetchHubDetails = async (hubId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getHubById(hubId);
      const hubDetails = (response as any)?.data || response;
      setHubData(hubDetails);
      
      if (hubDetails.state) {
        const stateId = parseInt(hubDetails.state.toString());
        if (!isNaN(stateId)) {
          fetchCitiesByState(stateId);
        }
      }
    } catch (error: any) {
      setError("Failed to load hub details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCitiesByState = async (stateId: number) => {
    setCitiesLoading(true);
    try {
      const response = await apiService.getCitiesByState(stateId);
      
      let citiesData: any[] = [];
      if (Array.isArray(response)) {
        citiesData = response;
      } else if (response && (response as any).data) {
        citiesData = (response as any).data;
      } else if (response && (response as any).result) {
        citiesData = (response as any).result;
      } else if (response && (response as any).cities) {
        citiesData = (response as any).cities;
      }
      
      const validCities = (citiesData || []).filter((city: any) => {
        return city && 
          typeof city === 'object' && 
          city.city_id !== undefined && 
          city.city_name !== undefined;
      });
      
      setCities(validCities);
    } catch (error) {
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const { options: hubStatusOptions } = useCodeCommon('HubStatus');

  const getStatusText = (status: any) => {
    const match = hubStatusOptions.find(option => option.codevalue === String(status));
    return match ? match.codedisplayname : String(status) || "Active";
  };

  const getStatusColor = (status: any) => {
    const text = getStatusText(status);
    if (text === 'Active') return 'rgba(212, 255, 199, 1)';
    if (text === 'Suspended' || text === 'Inactive') return 'rgba(255, 227, 227, 1)';
    if (text === 'Pending') return 'rgba(255, 245, 157, 1)';
    return '#f0f0f0';
  };

  const getStateName = (stateId: any) => {
    if (!stateId || !states.length) return "N/A";
    const state = states.find((s: any) => s.state_id === parseInt(stateId.toString()));
    return state ? state.state_name : "N/A";
  };

  const getCityName = (cityId: any) => {
    if (!cityId || !cities.length) return "N/A";
    const city = cities.find(c => c.city_id === parseInt(cityId.toString()));
    return city ? city.city_name : "N/A";
  };

  const getHubManagerName = (hubmanagerid: string): string => {
    if (!hubmanagerid || !hubManagers.length) {
      return "N/A";
    }
    
    const manager = hubManagers.find(manager => 
      manager.userid?.toString() === hubmanagerid.toString()
    );
    
    return manager?.name || "N/A";
  };

  const getHubManagerPhone = (hubmanagerid: string): string => {
    if (!hubmanagerid || !hubManagers.length) {
      return "N/A";
    }
    
    const manager = hubManagers.find(manager => manager.userid?.toString() === hubmanagerid.toString());
    return manager?.phoneNumber || "N/A";
  };

  const getHubPhoneByHubId = (hubmasterid: any): string => {
    if (!hubmasterid || !hubManagers.length) {
      return "N/A";
    }
    
    const manager = hubManagers.find(manager => 
      manager.hubid && hubmasterid && String(manager.hubid) === String(hubmasterid)
    );
    return manager?.phoneNumber || "N/A";
  };

  if (loading || hubManagersLoading) {
    return <CustomLoading message="Fetching Hub Details..." />;
  }

  if (error || hubManagersError) {
    return (
      <Container fluid>
        <Alert severity="error" className="mb-3">
          {error || hubManagersError}
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/hub-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!hubData) {
    return (
      <Container fluid>
        <Alert severity="warning" className="mb-3">
          No hub data found.
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/hub-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Box className="d-flex justify-content-between align-items-center mb-3">
            <Typography
              variant="h6"
              className="fw-bold mb-0"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Basic Information
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: getStatusColor(hubData.status),
                color: ((): string => {
                  const text = getStatusText(hubData.status);
                  if (text === 'Active') return 'rgba(31, 113, 13, 1)';
                  if (text === 'Suspended' || text === 'Inactive') return 'rgba(197, 0, 0, 1)';
                  if (text === 'Pending') return 'rgba(107, 105, 14, 1)';
                  return '#333';
                })(),
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            >
              {getStatusText(hubData.status)}
            </Box>
          </Box>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
            {[
              { label: "Hub Name", value: hubData.hubname || "N/A" },
              { label: "Phone Number", value: hubData.phonenumber || getHubPhoneByHubId(hubData.hubmasterid) || "N/A" },
              { label: "WhatsApp Number", value: hubData.whatsappnumber || "N/A" },
              { label: "State", value: getStateName(hubData.state) },
              { label: "City", value: getCityName(hubData.city) },
              { label: "Zip Code", value: hubData.zipcode || "N/A" },
              { label: "Address1", value: hubData.address1 || "N/A" },
              { label: "Address2", value: hubData.address2 || "N/A" },

            ].map((field, idx) => (
              <Col xs={12} sm={6} md={3} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Hub Management Details
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
            {[
              { label: "Hub Manager", value: getHubManagerName(hubData.hubmanagerid) },
              { label: "Manager Phone", value: getHubManagerPhone(hubData.hubmanagerid) },
              { label: "Operation Hours", value: hubData.operationhours || "N/A" },
            ].map((field, idx) => (
              <Col xs={12} sm={6} md={3} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Location Details
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
            {[
              { label: "Latitude", value: hubData.latitude ? parseFloat(hubData.latitude.toString()).toFixed(6) : "N/A" },
              { label: "Longitude", value: hubData.longitude ? parseFloat(hubData.longitude.toString()).toFixed(6) : "N/A" },
              { label: "Geofence Start Radius", value: hubData.geofencestartradius ? `${hubData.geofencestartradius} meters` : "N/A" },
              { label: "Geofence End Radius", value: hubData.geofencesendradius ? `${hubData.geofencesendradius} meters` : "N/A" },
            ].map((field, idx) => (
              <Col xs={12} sm={6} md={3} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Attachments
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-3">
            {hubData.hubimage && (
              <Col xs={12} sm={6} md={4}>
                <ImagePreviewCard
                  title="Hub Image"
                  value={hubData.hubimage}
                  resolveSrc={resolvePreviewSrc}
                  size={200}
                  defaultImage={DefaultImage}
                />
              </Col>
            )}

            {!hubData.hubimage && (
              <Col xs={12}>
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    borderRadius: 2,
                    border: "2px dashed #dee2e6",
                  }}
                >
                  <Typography variant="body1" sx={{ color: "#6c757d" }}>
                    No attachments available
                  </Typography>
                </Box>
              </Col>
            )}
          </Row>
        </Box>
      </Paper>

      <Paper
        className="mt-4 shadow-sm border-0 rounded-3"
        sx={{
          position: { xs: "fixed", md: "static" },
          bottom: { xs: 0, md: "auto" },
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
      </Paper>
    </Container>
  );
};


export default HubManagementDetails;
